import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { InfoIcon, LearnTip } from '../lib/learn-mode'
import { fmtMoney, FMT_INT } from '../lib/money'
import { useCurrency } from '../lib/currency-context'
import { STANDARD_PERIODS, type PeriodKey } from '../lib/period-context'

interface CustomerKPIs {
  active_customers: number
  churned_90d: number
  arpu_30d_cents: number
  total_ltv_cents: number
  total_customers_lifetime: number
  deposit_float_cents: number | null
  unbilled_adjustments_cents: number | null
  unbilled_adjustments_count: number | null
}

interface AtRiskRow {
  customer_name: string
  last_ship: string
  ships_90d: number
  profit_90d_cents: number
  days_silent: number
}

interface RosterRow {
  customer_name: string
  affiliate: string | null
  ships: number
  profit_cents: number
  avg_margin_cents: number
  last_activity: string
  billing_mode: 'deposit' | 'credit'
}

type Bucket = 'at-risk' | 'roster'

export function CustomersView() {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const [bucket, setBucket] = useState<Bucket>('at-risk')
  const [period, setPeriod] = useState<PeriodKey>('this_month')
  const queryClient = useQueryClient()
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  // Result of the last billing-mode change: error, warning, or what happened
  // to the ByeLabel push exclusion (credit adds it automatically).
  const [billingNotice, setBillingNotice] = useState<{ kind: 'error' | 'warn' | 'ok'; text: string } | null>(null)

  const billingModeMutation = useMutation({
    mutationFn: async ({ name, mode }: { name: string; mode: 'deposit' | 'credit' }) => {
      const r = await fetch(`/api/customers/${encodeURIComponent(name)}/billing-mode`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ billing_mode: mode }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok || !d?.ok) throw new Error(d?.error || `HTTP ${r.status}`)
      return d as { customer_name: string; billing_mode: string; push_exclusion: string | null; warning?: string }
    },
    onSuccess: d => {
      setOpenMenu(null)
      setBillingNotice(d.warning
        ? { kind: 'warn', text: `${d.customer_name}: ${d.warning}` }
        : d.billing_mode === 'credit'
          ? { kind: 'ok', text: `${d.customer_name} is now credit (invoiced) and excluded from the ByeLabel push.` }
          : { kind: 'ok', text: `${d.customer_name} is now deposit.` })
      queryClient.invalidateQueries({ queryKey: ['customers', 'roster'] })
    },
    onError: (e: Error) => setBillingNotice({ kind: 'error', text: `Billing mode not changed: ${e.message}` }),
  })

  const kpis = useQuery<CustomerKPIs>({
    queryKey: ['customers', 'kpis'],
    queryFn: () => fetch('/api/customers/kpis').then(r => r.json()),
  })
  const atRisk = useQuery<{ rows: AtRiskRow[] }>({
    queryKey: ['customers', 'at-risk'],
    queryFn: () => fetch('/api/customers/at-risk').then(r => r.json()),
    enabled: bucket === 'at-risk',
  })
  const roster = useQuery<{ rows: RosterRow[] }>({
    queryKey: ['customers', 'roster', period],
    queryFn: () => fetch(`/api/customers/roster?period=${period}`).then(r => r.json()),
    enabled: bucket === 'roster',
  })

  const k = kpis.data

  return (
    <div className="space-y-5">
      {billingNotice && (
        <div className={`flex items-start justify-between gap-3 px-4 py-2 rounded-lg border text-[13px] ${
          billingNotice.kind === 'error' ? 'bg-flame-50 border-flame-200 text-flame-700'
          : billingNotice.kind === 'warn' ? 'bg-honey-50 border-honey-200 text-honey-700'
          : 'bg-mint-50 border-mint-200 text-mint-700'}`}>
          <span>{billingNotice.text}</span>
          <button onClick={() => setBillingNotice(null)} className="shrink-0 opacity-70 hover:opacity-100">×</button>
        </div>
      )}
      {/* Hero */}
      <Card className="p-7">
        <div className="flex items-center justify-between gap-6">
          <div>
            <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">
              {t('customers.title')}<InfoIcon tip={t('customers.title_tip')} />
            </div>
            <div className="mt-2 text-[44px] font-bold text-ink-900 leading-none">
              {k ? FMT_INT.format(k.active_customers) : <span className="text-ink-300">—</span>}
              <span className="text-[28px] font-normal text-ink-400 ml-3">
                {t('customers.active')} · {k ? FMT_INT.format(k.churned_90d) : '—'} {t('customers.churned_90d')}
              </span>
            </div>
            <div className="mt-2 text-[13px] text-ink-500">
              {k?.deposit_float_cents !== null && k?.deposit_float_cents !== undefined && (
                <>
                  {fmtMoney(k.deposit_float_cents, currency, { compact: true })} deposit float
                  {' · '}
                </>
              )}
              {k?.unbilled_adjustments_cents !== null && k?.unbilled_adjustments_cents !== undefined && (
                <>{fmtMoney(k.unbilled_adjustments_cents, currency, { compact: true })} unbilled adjustments</>
              )}
            </div>
            <LearnTip>{t('customers.learn_tip')}</LearnTip>
          </div>
        </div>
      </Card>

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('customers.arpu_30d')}<InfoIcon tip={t('customers.arpu_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">{k ? fmtMoney(k.arpu_30d_cents, currency) : '—'}</div>
          <div className="text-[11px] text-ink-500 mt-1">{t('customers.per_active_customer')}</div>
          <LearnTip>{t('customers.arpu_learn')}</LearnTip>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('customers.total_ltv')}<InfoIcon tip={t('customers.ltv_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">{k ? fmtMoney(k.total_ltv_cents, currency, { compact: true }) : '—'}</div>
          <div className="text-[11px] text-ink-500 mt-1">
            {k ? `${FMT_INT.format(k.total_customers_lifetime)} customers since inception` : '—'}
          </div>
          <LearnTip>All profit ByeLabel has ever made, across every customer ever signed up.</LearnTip>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('customers.deposit_float_kpi')}<InfoIcon tip={t('customers.deposit_float_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">
            {k?.deposit_float_cents !== null && k?.deposit_float_cents !== undefined
              ? fmtMoney(k.deposit_float_cents, currency, { compact: true }) : '—'}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">{t('customers.held_across_customers')}</div>
          <LearnTip>{t('customers.deposit_float_learn')}</LearnTip>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
            {t('customers.unbilled_adj')}<InfoIcon tip={t('customers.unbilled_adj_tip')} />
          </div>
          <div className="mt-2 text-[24px] font-bold">
            {k?.unbilled_adjustments_cents !== null && k?.unbilled_adjustments_cents !== undefined
              ? fmtMoney(k.unbilled_adjustments_cents, currency, { compact: true }) : '—'}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">
            {k?.unbilled_adjustments_count ? `${FMT_INT.format(k.unbilled_adjustments_count)} adjustments` : 'no customer_rebills data'}
          </div>
          <LearnTip>Real money sitting unclaimed. Every day = more risk of being forgotten.</LearnTip>
        </Card>
      </div>

      {/* Bucket switcher */}
      <div className="flex gap-2">
        <button
          onClick={() => setBucket('at-risk')}
          className={`px-3.5 py-2 rounded-lg text-[13px] font-medium border flex items-center gap-2 ${
            bucket === 'at-risk' ? 'bg-ink-900 text-white border-ink-900' : 'bg-white text-ink-700 border-ink-200 hover:bg-ink-50'
          }`}
        >
          {t('customers.at_risk')}
          {atRisk.data && <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-flame-100 text-flame-700">{atRisk.data.rows.length}</span>}
        </button>
        <button
          onClick={() => setBucket('roster')}
          className={`px-3.5 py-2 rounded-lg text-[13px] font-medium border flex items-center gap-2 ${
            bucket === 'roster' ? 'bg-ink-900 text-white border-ink-900' : 'bg-white text-ink-700 border-ink-200 hover:bg-ink-50'
          }`}
        >
          {t('customers.full_roster')}
          {roster.data && <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-ink-100 text-ink-700">{roster.data.rows.length}</span>}
        </button>
      </div>

      {/* Table */}
      {bucket === 'at-risk' && (
        <Card className="p-5">
          <div className="text-[14px] font-semibold text-ink-800 mb-2">
            {t('customers.at_risk_title')}<InfoIcon tip={t('customers.at_risk_tip')} />
          </div>
          <LearnTip>{t('customers.at_risk_learn')}</LearnTip>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                  <th className="text-left py-2 pr-3">{t('customers.customer_col')}</th>
                  <th className="text-right py-2 pr-3">{t('customers.ships_90d_col')}</th>
                  <th className="text-right py-2 pr-3">{t('customers.profit_90d_col')}</th>
                  <th className="text-left py-2 pr-3">{t('customers.last_ship_col')}</th>
                  <th className="text-right py-2 pr-3">{t('customers.days_silent_col')}</th>
                </tr>
              </thead>
              <tbody>
                {atRisk.data?.rows.map(r => (
                  <tr key={r.customer_name} className="border-b border-ink-100 hover:bg-ink-50">
                    <td className="py-2 pr-3 font-medium">{r.customer_name}</td>
                    <td className="py-2 pr-3 text-right num">{FMT_INT.format(r.ships_90d)}</td>
                    <td className="py-2 pr-3 text-right num font-semibold">{fmtMoney(r.profit_90d_cents, currency, { compact: true })}</td>
                    <td className="py-2 pr-3 text-ink-500">{r.last_ship}</td>
                    <td className="py-2 pr-3 text-right num">
                      <span className={`pill ${r.days_silent > 30 ? 'pill-flame' : 'pill-honey'}`}>{r.days_silent}d</span>
                    </td>
                  </tr>
                ))}
                {atRisk.isLoading && <tr><td colSpan={5} className="py-6 text-center text-ink-400">{t('common.loading')}</td></tr>}
                {atRisk.data?.rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-ink-400">No at-risk customers — everyone shipping recently 🎉</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {bucket === 'roster' && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[14px] font-semibold text-ink-800">
{t('customers.full_roster')}<InfoIcon tip={t('customers.full_roster_tip')} />
            </div>
            <select value={period} onChange={e => setPeriod(e.target.value as PeriodKey)}
                    className="px-3 py-1.5 border border-ink-200 rounded-lg text-sm bg-white text-ink-700 font-medium">
              {STANDARD_PERIODS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                  <th className="text-left py-2 pr-3">{t('customers.customer_col')}</th>
                  <th className="text-left py-2 pr-3">{t('customers.affiliate_col')}</th>
                  <th className="text-right py-2 pr-3">{t('customers.ships_col')}</th>
                  <th className="text-right py-2 pr-3">{t('customers.profit_col')}</th>
                  <th className="text-right py-2 pr-3">{t('customers.avg_margin_col')}</th>
                  <th className="text-left py-2 pr-3">{t('customers.last_activity_col')}</th>
                  <th className="text-left py-2 pr-3">{t('customers.billing_col')}<InfoIcon tip={t('customers.billing_tip')} /></th>
                </tr>
              </thead>
              <tbody>
                {roster.data?.rows.map(r => {
                  const isDeposit = (r.billing_mode ?? 'deposit') === 'deposit'
                  const isPending = billingModeMutation.isPending &&
                    (billingModeMutation.variables as { name: string })?.name === r.customer_name
                  const isOpen = openMenu === r.customer_name
                  return (
                  <tr key={r.customer_name} className="border-b border-ink-100 hover:bg-ink-50">
                    <td className="py-2 pr-3 font-medium">{r.customer_name}</td>
                    <td className="py-2 pr-3">{r.affiliate ? <span className="pill pill-royal">{r.affiliate}</span> : <span className="text-ink-400 text-[11px]">direct</span>}</td>
                    <td className="py-2 pr-3 text-right num">{FMT_INT.format(r.ships)}</td>
                    <td className="py-2 pr-3 text-right num font-semibold">{fmtMoney(r.profit_cents, currency, { compact: true })}</td>
                    <td className="py-2 pr-3 text-right num text-ink-600">{fmtMoney(r.avg_margin_cents, currency)}</td>
                    <td className="py-2 pr-3 text-ink-500">{r.last_activity}</td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2 relative">
                        <span className={`pill ${isDeposit ? 'pill-honey' : 'pill-royal'}`}>
                          {isPending ? '…' : isDeposit ? 'Deposit' : 'Credit'}
                        </span>
                        <button
                          onClick={() => setOpenMenu(isOpen ? null : r.customer_name)}
                          className="text-ink-400 hover:text-ink-700 px-1 rounded"
                          title={t('customers.customer_settings')}
                        >
                          ⋯
                        </button>
                        {isOpen && (
                          <div className="absolute right-0 top-7 z-10 bg-white border border-ink-200 rounded-lg shadow-lg min-w-[160px] py-1"
                               onMouseLeave={() => setOpenMenu(null)}>
                            <div className="px-3 py-1.5 text-[11px] font-semibold text-ink-400 uppercase tracking-wider">
                              {t('customers.billing_type')}
                            </div>
                            {(['deposit', 'credit'] as const).map(mode => (
                              <button
                                key={mode}
                                disabled={isPending || r.billing_mode === mode}
                                onClick={() => billingModeMutation.mutate({ name: r.customer_name, mode })}
                                className={`w-full text-left px-3 py-2 text-[13px] hover:bg-ink-50 disabled:opacity-40
                                  ${r.billing_mode === mode ? 'font-semibold text-ink-900' : 'text-ink-700'}`}
                              >
                                {mode === 'deposit' ? '🟡 Deposit' : '🔵 Credit'}
                                {r.billing_mode === mode && ' ✓'}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  )
                })}
                {roster.isLoading && <tr><td colSpan={7} className="py-6 text-center text-ink-400">{t('common.loading')}</td></tr>}
                {roster.data?.rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-ink-400">No customers in this period</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}
